
import React, { useState, useEffect, useRef } from 'react';
import { UserRole, UserProfile, AgencyClient, BrandColor } from './types';
import { LandingPage } from './features/LandingPage';
import { Dashboard } from './features/Dashboard';
import { SettingsPanel } from './features/Settings';
import { AdminDashboard } from './features/AdminDashboard';
import { MediaLibrary } from './features/MediaLibrary';
import { Settings as SettingsIcon, LogOut, LayoutGrid, Loader2, Shield, Lock } from 'lucide-react';
import { Button, Input, Card } from './components/UI';
import { JOISST_API_URL, APP_URL, DEFAULT_COLORS } from './constants';
import { hexToCmyk } from './utils/svgHelpers';

const App: React.FC = () => {
  // State
  const [currentView, setCurrentView] = useState<'landing' | 'app' | 'admin' | 'media' | 'public'>('landing');
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [activeRole, setActiveRole] = useState<UserRole>(UserRole.USER);
  const [showSettings, setShowSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Auto-Save & History State
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error'>('saved');
  const [history, setHistory] = useState<UserProfile[]>([]);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Public View State
  const [publicProfile, setPublicProfile] = useState<any>(null);
  const [publicPassword, setPublicPassword] = useState('');
  const [isPublicLocked, setIsPublicLocked] = useState(false);

  // Agency State
  const [clients, setClients] = useState<AgencyClient[]>([]);
  const [dashboardUserId, setDashboardUserId] = useState<string>('');

  const activeDashboardUser = currentUser && dashboardUserId === currentUser.id 
    ? currentUser 
    : clients.find(c => c.id === dashboardUserId) || currentUser;

  // Migration Helper: Convert old object-style colors to new array-style
  const migrateColors = (user: any): BrandColor[] => {
    let colors: BrandColor[] = [];

    if (Array.isArray(user.colors)) {
      // Handle array migration (removing usage field if exists, keeping only name)
      colors = user.colors.map((c: any) => ({
        id: c.id,
        hex: c.hex,
        cmyk: c.cmyk,
        name: c.name || c.usage || 'Brand Color', // Merge usage into name if migrating
        visible: c.visible !== false
      }));
    } else {
      // Legacy object mapping
      const c = user.colors || {};
      const hidden = user.hiddenColorKeys || [];
      
      colors = [
        { id: 'primary', hex: c.primary || '#000000', name: 'Primary Brand Color', visible: !hidden.includes('primary') },
        { id: 'secondary', hex: c.secondary || '#ffffff', name: 'Secondary Brand Color', visible: !hidden.includes('secondary') },
        { id: 'accent', hex: c.accent || '#888888', name: 'Accent Color', visible: !hidden.includes('accent') },
        { id: 'text', hex: c.text || '#000000', name: 'Neutral', visible: !hidden.includes('text') },
        { id: 'background', hex: c.background || '#ffffff', name: 'Highlight Color', visible: !hidden.includes('background') },
      ];
    }

    // Ensure CMYK exists on all colors
    return colors.map(c => ({
      ...c,
      cmyk: c.cmyk || hexToCmyk(c.hex)
    }));
  };

  // INITIALIZATION CHECK
  useEffect(() => {
    const init = async () => {
      // 0. Run DB migrations automatically on startup to ensure schema matches code
      try {
        await fetch('/api/setup');
      } catch (e) {
        console.error("Migration/Setup error:", e);
      }

      const path = window.location.pathname;
      
      // 1. Check if we are viewing a public profile
      if (path.startsWith('/u/')) {
        const publicId = path.split('/u/')[1];
        if (publicId) {
          await loadPublicProfile(publicId);
        }
        setIsLoading(false);
        return;
      }

      // 2. Check Joisst Session via Local Proxy
      await checkJoisstSession();
      setIsLoading(false);
    };

    init();
  }, []);

  // Clear history when switching users
  useEffect(() => {
    setHistory([]);
  }, [dashboardUserId]);

  const checkJoisstSession = async () => {
    try {
      const res = await fetch('/api/auth?action=check', {
        method: 'GET'
      });

      if (res.ok) {
        const localUser = await res.json();
        
        // Ensure colors are migrated
        localUser.colors = migrateColors(localUser);

        setCurrentUser(localUser);
        setActiveRole(localUser.role as UserRole);
        setDashboardUserId(localUser.id);
        
        if (localUser.clients) {
          setClients(localUser.clients.map((c: any) => ({ 
            ...c, 
            role: UserRole.USER,
            colors: migrateColors(c) 
          })));
        }
        
        setCurrentView('app');
      } else {
        setCurrentView('landing');
      }
    } catch (error) {
      console.error("Session check error", error);
      setCurrentView('landing');
    }
  };

  const loadPublicProfile = async (id: string) => {
    try {
      const res = await fetch(`/api/public?id=${id}`);
      const data = await res.json();
      
      if (res.ok) {
        // Migrate public profile colors too
        data.colors = migrateColors(data);
        setPublicProfile({ ...data, id }); 
        setIsPublicLocked(data.isLocked && data.requiresAuth);
        setCurrentView('public');
      } else {
        alert("Brand not found");
        setCurrentView('landing');
      }
    } catch (e) {
      console.error(e);
      setCurrentView('landing');
    }
  };

  const unlockPublicProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`/api/public?id=${publicProfile.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: publicPassword })
      });
      const data = await res.json();
      if (res.ok) {
        data.colors = migrateColors(data);
        setPublicProfile({ ...data, id: publicProfile.id });
        setIsPublicLocked(false);
      } else {
        alert("Incorrect Password");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // --- AUTO SAVE & UNDO LOGIC ---

  const handleUserUpdate = (updatedUser: UserProfile) => {
    // 1. History & State Update
    if (activeDashboardUser) {
      setHistory(prev => {
        const newHist = [...prev, activeDashboardUser];
        // Limit history to last 50 steps
        if (newHist.length > 50) return newHist.slice(newHist.length - 50);
        return newHist;
      });
    }

    // Update Local State Immediately
    if (updatedUser.id === currentUser?.id) {
      setCurrentUser(updatedUser);
    } else {
      setClients(clients.map(c => c.id === updatedUser.id ? { ...c, ...updatedUser } as AgencyClient : c));
    }

    // 2. Debounced API Call
    setSaveStatus('saving');
    
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await fetch('/api/user', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedUser)
        });

        if (!res.ok) {
          const text = await res.text();
          // Alert user on specific failures like 413 Payload Too Large
          if (res.status === 413) {
             alert("Save Failed: Image files are too large. Please delete some items or try smaller files.");
          }
          throw new Error(`Server returned ${res.status} ${res.statusText}`);
        }

        setSaveStatus('saved');
      } catch (err) {
        console.error("Failed to save changes", err);
        setSaveStatus('error');
      }
    }, 1000); // 1 Second debounce
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    
    // Get previous state
    const previousState = history[history.length - 1];
    
    // Remove from history
    setHistory(prev => prev.slice(0, -1));
    
    // Apply previous state locally
    if (previousState.id === currentUser?.id) {
      setCurrentUser(previousState);
    } else {
      setClients(clients.map(c => c.id === previousState.id ? { ...c, ...previousState } as AgencyClient : c));
    }

    // Force save immediately (cancel any pending debounced save)
    setSaveStatus('saving');
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    fetch('/api/user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(previousState)
    })
    .then(async res => {
      if (!res.ok) {
        if (res.status === 413) alert("Undo Failed: Payload too large.");
        throw new Error('Save failed');
      }
      setSaveStatus('saved');
    })
    .catch(() => setSaveStatus('error'));
  };

  const handleLogout = () => {
    window.location.href = `${JOISST_API_URL}/logout?returnTo=${encodeURIComponent(APP_URL)}`;
  };

  // --- RENDER ---

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-brand-background"><Loader2 className="animate-spin w-10 h-10 text-brand-primary"/></div>;
  }

  if (currentView === 'landing') {
    return <LandingPage 
      onLogin={() => {}} 
      onSignup={() => {}} 
    />;
  }

  // PUBLIC VIEW RENDER
  if (currentView === 'public' && publicProfile) {
    if (isPublicLocked) {
      return (
        <div className="min-h-screen bg-brand-background flex items-center justify-center p-4">
          <Card className="max-w-sm w-full text-center p-8">
             <div className="mx-auto w-16 h-16 bg-brand-text text-white rounded-full flex items-center justify-center mb-6">
               <Lock size={32}/>
             </div>
             <h2 className="text-2xl font-bold mb-2">{publicProfile.companyName}</h2>
             <p className="text-brand-text/60 mb-6">This brand grid is password protected.</p>
             <form onSubmit={unlockPublicProfile} className="space-y-4">
               <Input 
                 type="password" 
                 placeholder="Enter Pin" 
                 value={publicPassword} 
                 onChange={e => setPublicPassword(e.target.value)}
                 className="text-center text-2xl tracking-widest"
               />
               <Button type="submit" className="w-full" isLoading={isLoading}>Unlock</Button>
             </form>
          </Card>
        </div>
      );
    }

    // Public Dashboard (Read Only)
    return (
      <div className="min-h-screen bg-white text-brand-text">
         {/* Public Nav */}
         <nav className="bg-white/80 backdrop-blur-md px-6 py-4 flex justify-between items-center shadow-sm sticky top-0 z-50">
           <h1 className="font-bold text-xl">{publicProfile.companyName}</h1>
           <div className="text-xs font-mono uppercase text-gray-400">Public View</div>
         </nav>
         
         <Dashboard 
           user={publicProfile} 
           role={UserRole.USER} 
           isPublicView={true} 
           onUpdateUser={() => {}} 
           onOpenMedia={() => {}}
         />
         <div className="text-center py-8 opacity-50 text-xs">
           Powered by BrandGrid
         </div>
      </div>
    );
  }

  if (!currentUser || !activeDashboardUser) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin"/></div>;

  // -- ADMIN VIEW --
  if (currentView === 'admin' && activeRole === UserRole.ADMIN) {
    return (
      <div className="min-h-screen bg-gray-50 text-brand-text">
        <AdminDashboard 
          currentUser={currentUser}
          onBack={() => setCurrentView('app')}
          onLoginAsUser={(user) => {
            setDashboardUserId(user.id);
            if (!clients.find(c => c.id === user.id)) {
              setClients([...clients, { ...user, assets: [], colors: migrateColors(user) }]); 
            }
            setCurrentView('app');
          }}
        />
      </div>
    );
  }

  // -- MEDIA VIEW --
  if (currentView === 'media') {
    return (
      <div className="min-h-screen bg-white text-brand-text">
        <MediaLibrary 
          assets={activeDashboardUser.assets}
          onBack={() => setCurrentView('app')}
          onDelete={(id) => {
             const newAssets = activeDashboardUser.assets.filter(a => a.id !== id);
             handleUserUpdate({ ...activeDashboardUser, assets: newAssets });
          }}
          onRestore={(id) => {
            const newAssets = activeDashboardUser.assets.map(a => a.id === id ? { ...a, deletedAt: null } : a);
            handleUserUpdate({ ...activeDashboardUser, assets: newAssets });
          }}
        />
      </div>
    );
  }

  // -- APP VIEW --
  return (
    <div className="min-h-screen bg-brand-background text-brand-text flex flex-col">
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-brand-text/5 px-6 py-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
           <div className="w-8 h-8 bg-brand-primary rounded-lg flex items-center justify-center text-white font-bold">
             <LayoutGrid size={18} />
           </div>
           <div>
             <h1 className="font-bold leading-none">BrandGrid</h1>
             <p className="text-[10px] text-brand-text/50 uppercase tracking-widest">
               {activeRole === UserRole.AGENCY ? 'Agency Console' : 'Asset Manager'}
             </p>
           </div>
        </div>
        
        {activeRole === UserRole.AGENCY && dashboardUserId !== currentUser.id && (
           <div className="hidden md:block bg-brand-secondary/30 px-4 py-1 rounded-full text-xs font-bold border border-brand-secondary">
             Editing Client: {activeDashboardUser.companyName}
           </div>
        )}

        <div className="flex items-center gap-2">
          {activeRole === UserRole.ADMIN && (
             <Button variant="ghost" className="text-xs bg-red-50 text-red-600 hover:bg-red-100" onClick={() => setCurrentView('admin')}>
               <Shield size={14}/> Admin
             </Button>
          )}

          {activeRole === UserRole.AGENCY && dashboardUserId !== currentUser.id && (
             <Button variant="ghost" className="text-xs" onClick={() => setDashboardUserId(currentUser.id)}>
               Back to My Brand
             </Button>
          )}
          <button 
            onClick={() => setShowSettings(true)}
            className="p-2 hover:bg-brand-text/5 rounded-full transition-colors relative"
          >
            <SettingsIcon size={24} />
            {activeRole === UserRole.ADMIN && <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full"></span>}
          </button>
        </div>
      </nav>

      <main className="flex-1 relative">
        <Dashboard 
          user={activeDashboardUser} 
          role={activeRole} 
          onUpdateUser={handleUserUpdate}
          onOpenMedia={() => setCurrentView('media')}
          // Undo & Save Props
          onUndo={handleUndo}
          canUndo={history.length > 0}
          saveStatus={saveStatus}
        />
      </main>

      {showSettings && (
        <SettingsPanel 
          currentUser={currentUser}
          currentRole={activeRole}
          clients={clients}
          onSwitchRole={(r) => { setActiveRole(r); setShowSettings(false); }}
          onSelectClient={(id) => { setDashboardUserId(id); setShowSettings(false); }}
        />
      )}
      
      <footer className="py-6 text-center text-brand-text/30 text-xs">
         <p>&copy; 2024 BrandGrid. All rights reserved.</p>
         <button onClick={handleLogout} className="flex items-center gap-1 mx-auto mt-2 hover:text-brand-primary">
           <LogOut size={10} /> Logout
         </button>
      </footer>
    </div>
  );
};

export default App;
